import classNames from 'classnames';
import { FormikContext } from 'formik';
import React, { useContext } from 'react';
import { labelFromClickEvent, useAsyncButton } from 'ud-ui/hooks/use-async-button';
import { BUTTON_TYPOGRAPHY_CLASS } from './button-metrics';
import { Button } from './styles';

export type ButtonVariant = 'primary' | 'secondary' | 'dark' | 'light' | 'gray' | 'green' | 'danger';
export type ButtonTertiaryVariant = 'tertiary';
export type ButtonSubVariant = 'for-dark' | 'for-light';

// Базовые пропсы без onClick
type BaseButtonProps = {
  slim?: boolean;
  children: React.ReactNode;
  stretch?: boolean;
} & Omit<React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>, 'children' | 'onClick'>;

// Пропсы для синхронного обработчика
interface SyncHandlerProps {
  type?: 'button';
  onClick?: (...args: any[]) => void;
  loading?: boolean;
}

// Пропсы для асинхронного обработчика: кнопку запирает его промис, внешний `loading` — только когда запрос идёт
// не отсюда (IMGM-1561).
interface AsyncHandlerProps {
  type?: 'button';
  onClick: (...args: any[]) => Promise<any>;
  loading?: boolean;
}

// Пропсы для кнопки отправки формы
interface SubmitButtonProps {
  type: 'submit';
  onClick?: never;
  /**
   * Внутри Formik не нужен: кнопка запирается сама, пока `isSubmitting` (`onSubmit` обязан вернуть промис
   * запроса, иначе Formik не сбросит флаг). Передаётся только для формы без Formik или когда запрос идёт не
   * из `onSubmit`.
   */
  loading?: boolean;
}

type ButtonCommonProps = BaseButtonProps & (SyncHandlerProps | AsyncHandlerProps | SubmitButtonProps);

type TertiaryVariantProps = {
  variant: ButtonTertiaryVariant;
  subVariant: ButtonSubVariant;
} & ButtonCommonProps;

type CommonVariantProps = {
  variant: ButtonVariant;
  subVariant?: undefined;
} & ButtonCommonProps;

export type UDButtonProps = TertiaryVariantProps | CommonVariantProps;

const UDButton = React.forwardRef<HTMLButtonElement, UDButtonProps>((props, ref) => {
  const {
    slim,
    loading: externalLoading,
    className,
    variant,
    subVariant,
    onClick,
    disabled,
    type = 'button',
    stretch,
    ...otherProps
  } = props;

  const { isLoading: isClickLoading, handleClick } = useAsyncButton(type === 'submit' ? undefined : onClick, {
    label: (event?: unknown) => labelFromClickEvent(event) ?? 'UDButton',
  });
  // `useContext`, а не `useFormikContext`: тот предупреждает в консоль, если кнопка стоит вне формы.
  const formik = useContext(FormikContext) as React.ContextType<typeof FormikContext> | undefined;
  const isLoading = isClickLoading || (type === 'submit' && Boolean(formik?.isSubmitting));
  const isDisabled = disabled || isLoading || externalLoading;

  const buttonClassName = classNames(
    variant,
    subVariant,
    className,
    {
      slim,
      stretch,
      loading: isLoading || externalLoading,
    },
    BUTTON_TYPOGRAPHY_CLASS,
  );

  return (
    <Button
      {...otherProps}
      ref={ref}
      type={type}
      className={buttonClassName}
      onClick={type === 'submit' ? undefined : handleClick}
      disabled={isDisabled}
      aria-busy={isLoading || externalLoading || undefined}
    >
      {props.children}
    </Button>
  );
});

export default UDButton;
